import { FieldsContext } from '@/contexts/FieldsContext'
import { RichMentionsProvider } from '@flatfile/baseline'
import { useContext, useEffect, useMemo, useRef } from 'react'

const getConfigs = (data: Record<string, string>[]) => {
  return [
    {
      query: /@([a-zA-Z0-9_-]+)?/,
      match: /(@\w+)\|([^>]+)/g,
      matchDisplay: '$1',
      customizeFragment: (fragment: HTMLSpanElement, final: boolean) => {
        fragment.className = `bg-gray-${
          final ? '100' : '50'
        } rounded-sm px-1 py-[1px]`
      },
      onMention: (text: string) => {
        const search = text.substr(1) // remove '@'
        return data.filter(
          (item: Record<string, string>) =>
            !search || item.name.toLowerCase().includes(search.toLowerCase())
        )
      },
    },
  ]
}

export const MentionsWrapper = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const { mentions } = useContext(FieldsContext)

  const configs = useMemo(() => {
    return mentions.length > 0 ? getConfigs(mentions) : []
  }, [mentions])

  // Use a reference to track if mentions have changed
  const prevMentionsRef = useRef(mentions)
  const mentionsContextRef = useRef(null)

  // Add an effect to update configs in the context when mentions change
  useEffect(() => {
    // Check if mentions have actually changed (comparing refs isn't reliable)
    const mentionsChanged =
      JSON.stringify(prevMentionsRef.current) !== JSON.stringify(mentions)

    if (mentionsChanged && mentionsContextRef.current?.configs) {
      // Update the configs in the RichMentionsContext
      mentionsContextRef.current.configs = configs

      // Update our reference for next comparison
      prevMentionsRef.current = mentions
    }
  }, [mentions, configs])

  // Keep the stable key to prevent unnecessary remounts
  const mentionsKey = `mentions-provider-instance`

  return (
    <RichMentionsProvider
      configs={configs as any}
      key={mentionsKey}
      getContext={mentionsContextRef}
    >
      {children}
    </RichMentionsProvider>
  )
}
